import { ArrowLeft, Save, Eye, Download, Trash, Sparkles, LayoutTemplate, Undo2, Redo2, Monitor, Smartphone, PanelLeftClose, PanelLeftOpen, ClipboardPaste, Clock, Moon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useBuilder } from '../context/BuilderContext'
import { useState, useEffect } from 'react'
import { exportToHTML } from '../utils/htmlExporter'
import { saveVersion } from './VersionHistoryModal'
import axios from '../../../lib/axios'

export default function TopBar({ templateId, onOpenAIModal, onOpenTemplatePicker, previewMode, onTogglePreview, toolboxCollapsed, onToggleToolbox, onOpenVersionHistory }) {
  const { subject, setSubject, blocks, bodySettings, clearAll, undo, redo, canUndo, canRedo, addBlock } = useBuilder()
  const [saving, setSaving] = useState(false)

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        undo()
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault()
        redo()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [undo, redo])

  const handleSave = async () => {
    if (!subject.trim()) {
      alert('Subject is required')
      return
    }

    setSaving(true)
    try {
      const html = exportToHTML(blocks, subject, bodySettings)
      const templateData = { blocks, subject, bodySettings }

      await axios.put(`master-templates/update/${templateId}`, {
        subject: subject.trim(),
        html,
        html_json: templateData
      })

      // Save version history
      saveVersion(templateId, blocks, subject, bodySettings)
      alert('Template saved successfully')
    } catch (err) {
      console.error(err)
      alert('Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  const handlePreview = () => {
    const html = exportToHTML(blocks, subject, bodySettings)
    const previewWindow = window.open('', '_blank')
    previewWindow.document.write(html)
    previewWindow.document.close()
  }

  const handleExport = () => {
    const html = exportToHTML(blocks, subject, bodySettings)
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${subject || 'email'}.html`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleClear = () => {
    if (confirm('Clear all blocks? This cannot be undone.')) {
      clearAll()
    }
  }

  return (
    <div className="editor-header">
      <Link to="/templates" style={{ flexShrink: 0 }}>
        <button className="icon-btn">
          <ArrowLeft size={20} />
        </button>
      </Link>

      <button
        className="icon-btn"
        onClick={onToggleToolbox}
        title={toolboxCollapsed ? 'Show components' : 'Hide components'}
        style={{ flexShrink: 0 }}
      >
        {toolboxCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
      </button>

      <input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Enter Subject"
        style={{ flex: 1, maxWidth: '280px' }}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginLeft: 'auto', flexShrink: 0 }}>
        {/* Undo/Redo */}
        <button
          className="icon-btn"
          onClick={undo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
          style={{ opacity: canUndo ? 1 : 0.35 }}
        >
          <Undo2 size={17} />
        </button>
        <button
          className="icon-btn"
          onClick={redo}
          disabled={!canRedo}
          title="Redo (Ctrl+Y)"
          style={{ opacity: canRedo ? 1 : 0.35 }}
        >
          <Redo2 size={17} />
        </button>

        <button
          className="icon-btn"
          onClick={async () => {
            try {
              const text = await navigator.clipboard.readText()
              const blockData = JSON.parse(text)
              if (blockData.type && blockData.settings) {
                const newBlock = {
                  id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  type: blockData.type,
                  settings: blockData.settings
                }
                addBlock(newBlock)
              } else {
                alert('Clipboard does not contain a valid block')
              }
            } catch {
              alert('No block data found in clipboard')
            }
          }}
          title="Paste block from clipboard"
        >
          <ClipboardPaste size={17} />
        </button>

        <div style={{ width: '1px', height: '20px', backgroundColor: '#e5e7eb', margin: '0 0.25rem' }} />

        {/* Responsive Preview Toggle */}
        <button
          className="icon-btn"
          onClick={() => onTogglePreview && onTogglePreview('desktop')}
          title="Desktop view"
          style={{
            color: previewMode === 'desktop' || !previewMode ? 'var(--color-primary)' : '#94a3b8',
            background: previewMode === 'desktop' || !previewMode ? 'rgba(99,102,241,0.08)' : 'transparent'
          }}
        >
          <Monitor size={17} />
        </button>
        <button
          className="icon-btn"
          onClick={() => onTogglePreview && onTogglePreview('mobile')}
          title="Mobile view"
          style={{
            color: previewMode === 'mobile' ? 'var(--color-primary)' : '#94a3b8',
            background: previewMode === 'mobile' ? 'rgba(99,102,241,0.08)' : 'transparent'
          }}
        >
          <Smartphone size={17} />
        </button>
        <button
          className="icon-btn"
          onClick={() => onTogglePreview && onTogglePreview(previewMode === 'dark' ? 'desktop' : 'dark')}
          title="Dark mode preview"
          style={{
            color: previewMode === 'dark' ? '#fbbf24' : '#94a3b8',
            background: previewMode === 'dark' ? 'rgba(251,191,36,0.1)' : 'transparent'
          }}
        >
          <Moon size={17} />
        </button>

        <div style={{ width: '1px', height: '20px', backgroundColor: '#e5e7eb', margin: '0 0.25rem' }} />

        <button
          className="btn btn-ghost"
          onClick={onOpenTemplatePicker}
          style={{ border: '1px solid #c7d2fe', color: '#6366f1', padding: '0.4rem 0.75rem' }}
        >
          <LayoutTemplate size={15} />
          <span>Templates</span>
        </button>

        <button
          className="btn btn-ghost"
          onClick={onOpenAIModal}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '0.4rem 0.75rem'
          }}
        >
          <Sparkles size={15} />
          <span>AI</span>
        </button>

        <button className="btn btn-ghost" onClick={handlePreview} disabled={blocks.length === 0} style={{ padding: '0.4rem 0.75rem' }}>
          <Eye size={15} />
          <span>Preview</span>
        </button>

        <button className="btn btn-ghost" onClick={handleExport} disabled={blocks.length === 0} style={{ padding: '0.4rem 0.75rem' }}>
          <Download size={15} />
          <span>Export</span>
        </button>

        <button className="btn btn-ghost" onClick={handleClear} disabled={blocks.length === 0} style={{ padding: '0.4rem 0.75rem' }}>
          <Trash size={15} />
          <span>Clear</span>
        </button>

        <button className="btn btn-ghost" onClick={onOpenVersionHistory} style={{ padding: '0.4rem 0.75rem' }}>
          <Clock size={15} />
          <span>History</span>
        </button>

        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving || !subject.trim()}
        >
          <Save size={15} />
          <span>{saving ? 'Saving...' : 'Save'}</span>
        </button>
      </div>
    </div>
  )
}
